import { rearrangeDaysSpanningCarRentals } from ".";
import { ActivityCarRentalForOutput, ActivityExperience, ActivityFerryTicket, ActivityMuseum, ActivityNcc, ActivityTrainTicket, ActivityTripComponent, ActivityType, ActivityWithTime, TripDay } from "../../../models";

interface ActivityOutputGenerationStrategy<T extends ActivityWithTime> {
    generateInfoSection(activity: T): string;
}

class ExperienceActivityOutputGenerationStrategy implements ActivityOutputGenerationStrategy<ActivityExperience> {
    generateInfoSection(activity: ActivityExperience): string {
        let experienceActivityHtml = "";
        
        if (activity.guide) {
            experienceActivityHtml = `<p><b>Persona di Contatto:</b> ${activity.guide}</p>`;
        }
        if (activity.meetingPoint) {
            experienceActivityHtml += `
                <p>
                    <b>Punto d'Incontro:</b>
                    <a href="${activity.meetingPoint}"
                       target="_blank"
                       rel="noopener noreferrer">
                        link
                    </a>
                </p>`;
        }
        if (activity.phone) {
            experienceActivityHtml += `<p><b>Numero di Telefono:</b> ${activity.phone}</p>`;
        }
        if (activity.duration) {
            const duration = activity.duration === '0.5h' ? "Mezz'ora" : activity.duration;
            experienceActivityHtml += `<p><b>Durata:</b> ${duration} (Circa)</p>`;
        }
        return experienceActivityHtml;
    }
}

class NccServiceActivityOutputGenerationStrategy implements ActivityOutputGenerationStrategy<ActivityNcc> {
    generateInfoSection(activity: ActivityNcc): string {
        let nccActivityHtml = "";
        
        if (activity.name) {
            nccActivityHtml = `<p><b>Persona di Contatto:</b> ${activity.name}</p>`;
        }
        if (activity.startingPoint) {
            nccActivityHtml += `<p><b>Punto d'Incontro:</b> ${activity.startingPoint}</p>`;
        }
        if (activity.arrivalPoint) {
            nccActivityHtml += `<p><b>Punto di Arrivo:</b> ${activity.arrivalPoint}</p>`;
        }
        if (activity.phone) {
            nccActivityHtml += `<p><b>Numero di Telefono:</b> ${activity.phone}</p>`;
        }
        return nccActivityHtml;
    }
}

class MuseumActivityOutputGenerationStrategy implements ActivityOutputGenerationStrategy<ActivityMuseum> {
    generateInfoSection(activity: ActivityMuseum): string {
        let museumActivityHtml = "";
        
        if (activity.address) {
            museumActivityHtml += `
                <p>
                    <b>Punto d'Incontro:</b>
                    <a href="${activity.address}"
                       target="_blank"
                       rel="noopener noreferrer">
                        link
                    </a>
                </p>`;
        }
        return museumActivityHtml;
    }
}

class CarRentalActivityOutputGenerationStrategy implements ActivityOutputGenerationStrategy<ActivityCarRentalForOutput> {
    generateInfoSection(activity: ActivityCarRentalForOutput): string {
        let carRentalActivityHtml = "";
        
        if (activity.isPickup) {
            if (activity.vectorName) {
                carRentalActivityHtml = `<p><b>Nome Compagnia:</b> ${activity.vectorName}</p>`;
            }
            if (activity.carType) {
                carRentalActivityHtml += `<p><b>Tipo di Auto:</b> ${activity.carType} o simile</p>`;
            }
            if (activity.meetingPoint) {
                carRentalActivityHtml += `
                    <p>
                        <b>Luogo di Ritiro Auto:</b>
                        <a href="${activity.meetingPoint}"
                           target="_blank"
                           rel="noopener noreferrer">
                            link
                        </a>
                    </p>`;
            }
            if (activity.phone) {
                carRentalActivityHtml += `<p><b>Numero di Telefono:</b> ${activity.phone}</p>`;
            }
            if (activity.whatIsIncluded) {
                carRentalActivityHtml += `<p><b>Cosa è Incluso:</b> ${sanitizeTextForHtml(activity.whatIsIncluded)}</p>`;
            }
            if (activity.depositAmount) {
                carRentalActivityHtml += `<p><b>Deposito da Pagare:</b> €${activity.depositAmount}</p>`;
            }
        }
        else if (activity.isDropOff) {
            if (activity.vectorName) {
                carRentalActivityHtml = `<p><b>Nome Compagnia:</b> ${activity.vectorName}</p>`;
            }
            if (activity.carType) {
                carRentalActivityHtml += `<p><b>Tipo di Auto:</b> ${activity.carType} o simile</p>`;
            }
            if (activity.returnPoint) {
                carRentalActivityHtml += `
                    <p>
                        <b>Luogo di Riconsegna Auto:</b>
                        <a href="${activity.returnPoint}"
                           target="_blank"
                           rel="noopener noreferrer">
                            link
                        </a>
                    </p>`;
            }
        }
        return carRentalActivityHtml;
    }
}

class TrainActivityOutputGenerationStrategy implements ActivityOutputGenerationStrategy<ActivityTrainTicket> {
    generateInfoSection(activity: ActivityTrainTicket): string {
        let trainActivityHtml = "";
        
        if (activity.departureStation) {
            trainActivityHtml = `<p><b>Stazione di Partenza:</b> ${activity.departureStation}</p>`;
        }
        if (activity.arrivalStation) {
            trainActivityHtml += `<p><b>Stazione di Arrivo:</b> ${activity.arrivalStation}</p>`;
        }
        if (activity.trainNumber) {
            trainActivityHtml += `<p><b>Numero Treno:</b> ${activity.trainNumber}</p>`;
        }
        if (activity.carriage) {
            trainActivityHtml += `<p><b>Numero Carrozza:</b> ${activity.carriage}</p>`;
        }
        if (activity.seatNumber) {
            trainActivityHtml += `<p><b>Numero Posto:</b> ${activity.seatNumber}</p>`;
        }
        return trainActivityHtml;
    }
}

class FerryActivityOutputGenerationStrategy implements ActivityOutputGenerationStrategy<ActivityFerryTicket> {
    generateInfoSection(activity: ActivityFerryTicket): string {
        let ferryActivityHtml = "";
        
        if (activity.companyName) {
            ferryActivityHtml = `<p><b>Nome Compagnia:</b> ${activity.companyName}</p>`;
        }
        if (activity.departurePort) {
            ferryActivityHtml += `<p><b>Porto di Partenza:</b> ${activity.departurePort}</p>`;
        }
        if (activity.arrivalPort) {
            ferryActivityHtml += `<p><b>Porto di Arrivo:</b> ${activity.arrivalPort}</p>`;
        }
        return ferryActivityHtml;
    }
}

class TripComponentGenerationStrategy implements ActivityOutputGenerationStrategy<ActivityTripComponent> {

    generateInfoSection(activity: ActivityTripComponent): string {
        let tripComponentHtml = "";
        // the description is optional
        return tripComponentHtml;
    }
}

/**
 * This class is responsible for generating the output for different types of activities.
 * 
 * It uses the Strategy pattern to handle different activity types and generate their respective output sections.
 */
export class TripOutputActivityGenerator {

    strategies: Map<ActivityType, ActivityOutputGenerationStrategy<any>>;
    constructor() {
        this.strategies = new Map<ActivityType, ActivityOutputGenerationStrategy<any>>([
            ["experience", new ExperienceActivityOutputGenerationStrategy()],
            ["ncc", new NccServiceActivityOutputGenerationStrategy()],
            ["museum", new MuseumActivityOutputGenerationStrategy()],
            ["car-rental", new CarRentalActivityOutputGenerationStrategy()],
            ["train-ticket", new TrainActivityOutputGenerationStrategy()],
            ["ferry-ticket", new FerryActivityOutputGenerationStrategy()],
            ["trip-component", new TripComponentGenerationStrategy()]
        ]);
    }


    /**
     * Generates the name of the activity passed as argument. It specifies the type of activity before the name.
     * 
     */
    generateActivityName(activity: ActivityWithTime): string {
        const nameMap: Map<ActivityType, string> = new Map([
            ["experience", `Esperienza: ${activity.name}`],
            ["ncc", `Trasporto Privato: ${activity.name}`],
            ["museum", `Visita: ${activity.name}`],
            ["car-rental", `Noleggio Auto: ${activity.name}`],
            ["train-ticket", `Trasferimento in Treno: ${activity.name}`],
            ["ferry-ticket", `Trasferimento in Traghetto: ${activity.name}`],
            // trip component generic name
            ["trip-component", `${activity.name}`]
        ])

        const label = nameMap.get(activity.type);
        if (label === undefined) {

            console.error(`Error during activity name generation. Unsupported activity type: ${activity.type} for activity: `, activity);
            return '';
        }
        return `<span class="schedule-activity">${label}</span>`;
    }

    /**
     * Generates the information section of the activity passed as argument.
     * 
     * The section is specific an 'activity related' and it follows the particular fields defined on each activity.
     */
    generateActivityInfoSection(activity: ActivityWithTime): string {
        const strategy = this.strategies.get(activity.type);
        if (!strategy) {
            console.error(`Error during activity info section generation. Unsupported activity type: ${activity.type} for activity: `, activity);
            return '';
        }

        return strategy.generateInfoSection(activity);
    }
}


/**
 * Performs an additional processing to the trip days before generating the final output.
 * @param days TripDay[]
 * @returns 
 */
export function processTripDaysBeforeOutputGeneration(days: TripDay[]): TripDay[] {
    // copy the array of days in order to mutate it
    // This avoids "TypeError: Cannot add property, object is not extensible" when pushing to activities
    const copiedDays: TripDay[] = JSON.parse(JSON.stringify(days));
    rearrangeDaysSpanningCarRentals(copiedDays);
    return copiedDays;
}

/**
 * Sanitizes the text for HTML output by removing any HTML tags and replacing line breaks with spaces.
 * @param text The input text to be sanitized.
 * @returns The sanitized text that is safe for HTML output.
 */
export function sanitizeTextForHtml(text: string | null): string {
    if (!text) {
        return '';
    }
    let sanitazedText = text.replace(/<br\s*\/?>/gi, ' ')  // replace <br> tags with a space
        .replace(/<\/?[^>]+(>|$)/g, '') // remove any remaining HTML tags
        .replace(/\s+/g, ' ') // normalize whitespace to a single space
        .trim();
    return sanitazedText;
}